// Finger Beam (owner 2026-11-02: 'a quick straight line piercing through multiple enemies, kind of like a short blast, but
// have like a long red tail'). Render-only reaction to the sim's 'finger' event (the needle is already resolved there):
// a razor-thin camera-facing ribbon that shoots out of the fingertip to its end in ~3 frames with a white-hot core and a
// crimson sheath, then cools into a long deep-red tail that thins and fades from the hero end toward the tip over ~0.5 s.
// A tiny muzzle flash at the fingertip and small crimson spark pops on every pierced foe; no end explosion, no bloom wash.
// One pooled dynamic mesh (a triangle strip per needle); no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, attribute, mix, smoothstep, uv, vec3 } from 'three/tsl';
import type { Fx } from './fx';
import { FLASH } from './fx';
import { uploadPrefix } from './uploadTrim';

const POOL = 24;        // needles alive at once (rank 5 Scarlet Fan: 3 shots × 3 needles, two volleys overlapping)
const SEG = 28;         // segments along a needle (the tail fades per point, so it needs some resolution)
export const HEAD_T = 0.05;    // the needle runs from the fingertip to its end in this time (s)
const HOT_T = 0.1;      // white-hot flash, then it cools into the red tail (s)
export const FADE_START = 0.07, FADE_SPAN = 0.3, FADE_LEN = 0.2; // point u (0 hand → 1 tip) fades over [start + u·span, … + len]
export const FINGER_LIFE = FADE_START + FADE_SPAN + FADE_LEN; // 0.57 s at the tip
const W_HOT = 0.34, W_TAIL = 0.26; // ribbon width (m) while hot / at the start of the tail

export interface Needle { ax: number; ay: number; az: number; bx: number; by: number; bz: number; t: number; on: boolean }

const _hot = new THREE.Color(0xff1434), _tail = new THREE.Color(0xd00a24), _coreTail = new THREE.Color(0xff5a6c);

export class FingerBeamFx {
  readonly mesh: THREE.Mesh;
  /** live needles (read by the ground glow, groundGlow.ts) */
  readonly needles: Needle[] = [];
  private geo = new THREE.BufferGeometry();
  private pos: Float32Array; private edge: Float32Array; private core: Float32Array;
  /** QA: freeze every needle at this age (s) for captures; null = run normally */
  hold: number | null = null;
  /** QA: needles fired so far */
  fired = 0;

  constructor(scene: THREE.Scene) {
    const vPer = (SEG + 1) * 2, nV = POOL * vPer;
    this.pos = new Float32Array(nV * 3); this.edge = new Float32Array(nV * 4); this.core = new Float32Array(nV * 4);
    const uvs = new Float32Array(nV * 2), idx = new Uint32Array(POOL * SEG * 6);
    for (let n = 0; n < POOL; n++) {
      for (let i = 0; i <= SEG; i++) { const v = n * vPer + i * 2; uvs.set([i / SEG, 0, i / SEG, 1], v * 2); }
      for (let i = 0; i < SEG; i++) { const v = n * vPer + i * 2, o = (n * SEG + i) * 6; idx.set([v, v + 1, v + 2, v + 2, v + 1, v + 3], o); }
      this.needles.push({ ax: 0, ay: 0, az: 0, bx: 0, by: 0, bz: 0, t: 9, on: false });
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('nedge', new THREE.BufferAttribute(this.edge, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('ncore', new THREE.BufferAttribute(this.core, 4).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const e = attribute('nedge', 'vec4'), c = attribute('ncore', 'vec4'); // e: sheath rgb + alpha; c: core rgb + core half-width
    const across = abs(uv().y.sub(0.5)).mul(2);                         // 0 on the centre line, 1 at the ribbon edge
    const coreMask = smoothstep(c.w, c.w.mul(0.6), across);             // hard cel core
    m.colorNode = mix(vec3(e.x, e.y, e.z), vec3(c.x, c.y, c.z), coreMask);
    m.opacityNode = smoothstep(1.0, 0.88, across).mul(e.w); // crisp edge: a soft one blends to brown over the grass
    this.mesh = new THREE.Mesh(this.geo, m); this.mesh.frustumCulled = false; this.mesh.renderOrder = 5;
    scene.add(this.mesh);
  }

  /** A needle fired from the fingertip (x,y,z) to (ex,ey,ez); hits = pierced foes (x,y,z triples). Small muzzle flash and
   *  crimson spark pops on every foe. */
  fire(x: number, y: number, z: number, ex: number, ey: number, ez: number, hits: number[], fan: number, fx: Fx, fromTip = false): void {
    const n = this.needles.reduce((a, b) => (!b.on ? (a.on ? b : a) : !a.on ? a : b.t > a.t ? b : a));
    Object.assign(n, { ax: x, ay: y, az: z, bx: ex, by: ey, bz: ez, t: 0, on: true });
    this.fired++;
    const L = Math.hypot(ex - x, ez - z) || 1, dx = (ex - x) / L, dz = (ez - z) / L;
    if (fan === 0 && !fromTip) { // one muzzle per shot (fan needles share the fingertip); hero shots: fingerCharge.ts draws the starburst
      fx.stars.add(x, y, z, 0.75, 0.07, 0xff6a7c, 2.4);
      fx.stars.add(x, y, z, 0.35, 0.05, 0xffffff, 2.6);
      for (let k = 0; k < 4; k++) fx.particles.emitDir(x, y, z, dx * (6 + Math.random() * 4) + (Math.random() - 0.5) * 3, Math.random() * 2, dz * (6 + Math.random() * 4) + (Math.random() - 0.5) * 3, 0xff3048, 0.16, 0.14, 2.2);
      if (FLASH.dim >= 1) fx.lights.flash(x, y, z, 0xff2040, 8, 0.08, 5);
    }
    for (let i = 0; i + 2 < hits.length; i += 3) { // pierced foe: a tiny crimson pop and sparks thrown along the needle
      const hx = hits[i], hy = hits[i + 1] + 0.1, hz = hits[i + 2];
      fx.stars.add(hx, hy, hz, 1.0, 0.09, 0xff4058, 2.4);
      for (let k = 0; k < 5; k++) {
        const sp = 4 + Math.random() * 5, sx = (Math.random() - 0.5) * 5, sz = (Math.random() - 0.5) * 5;
        fx.particles.emitDir(hx, hy, hz, dx * sp + sx, 1 + Math.random() * 3, dz * sp + sz, k % 2 ? 0xff2a40 : 0xff8a96, 0.2, 0.22, 2);
      }
    }
  }

  update(dt: number, cam: THREE.Vector3): void {
    const vPer = (SEG + 1) * 2, P = this.pos, E = this.edge, C = this.core;
    let drawn = 0;
    for (const n of this.needles) {
      if (!n.on) continue;
      n.t = this.hold ?? n.t + dt;
      const t = n.t;
      if (t > FINGER_LIFE) { n.on = false; continue; }
      const dx = n.bx - n.ax, dy = n.by - n.ay, dz = n.bz - n.az, L = Math.hypot(dx, dy, dz) || 1;
      const head = Math.min(1, t / HEAD_T);               // how far the needle has run out of the finger
      const cool = Math.min(1, Math.max(0, (t - HOT_T * 0.4) / (HOT_T * 0.6))); // 0 white-hot → 1 red tail
      const v0 = drawn * vPer;
      for (let i = 0; i <= SEG; i++) {
        const u = i / SEG, uu = Math.min(u, head);       // points past the head collapse onto it (zero width)
        const px = n.ax + dx * uu, py = n.ay + dy * uu, pz = n.az + dz * uu;
        // per-point life: the hand end goes first, the far tip lingers longest
        const f = 1 - Math.min(1, Math.max(0, (t - FADE_START - u * FADE_SPAN) / FADE_LEN));
        const shown = u <= head ? 1 : 0;
        const taper = Math.min(1, u * L / 0.25, (1 - u) * L / 0.6 + 0.15); // sharp needle point, pinched at the finger
        const w = (W_HOT + (W_TAIL - W_HOT) * cool) * (0.12 + 0.88 * Math.pow(f, 0.7)) * taper * shown; // the tail thins as it fades
        // camera-facing side vector
        const vx = cam.x - px, vy = cam.y - py, vz = cam.z - pz;
        let sx = dy * vz - dz * vy, sy = dz * vx - dx * vz, sz = dx * vy - dy * vx;
        const sl = Math.hypot(sx, sy, sz) || 1, hw = w * 0.5 / sl; sx *= hw; sy *= hw; sz *= hw;
        const o = (v0 + i * 2) * 3;
        P[o] = px - sx; P[o + 1] = py - sy; P[o + 2] = pz - sz; P[o + 3] = px + sx; P[o + 4] = py + sy; P[o + 5] = pz + sz;
        // colour: bright crimson sheath + white core while hot, cooling to a deep red tail with a thin hot-pink core
        const lift = 1 + 0.25 * (1 - cool);
        const er = (_hot.r + (_tail.r - _hot.r) * cool) * lift, eg = (_hot.g + (_tail.g - _hot.g) * cool) * lift, eb = (_hot.b + (_tail.b - _hot.b) * cool) * lift;
        const cr = 1 + (_coreTail.r - 1) * cool, cg = 1 + (_coreTail.g - 1) * cool, cb = 1 + (_coreTail.b - 1) * cool;
        const cw = Math.max(0.04, (0.4 - 0.24 * cool) * Math.min(1, f * 1.6));
        const a = Math.min(1, f * 2.5) * 0.97; // stays solid red while it thins, then drops out
        for (let s = 0; s < 2; s++) {
          const q = (v0 + i * 2 + s) * 4;
          E[q] = er; E[q + 1] = eg; E[q + 2] = eb; E[q + 3] = a;
          C[q] = cr; C[q + 1] = cg; C[q + 2] = cb; C[q + 3] = cw;
        }
      }
      // the strip indices are laid out per pool slot: draw slots in order, so pack this needle into slot `drawn`
      drawn++;
    }
    this.geo.setDrawRange(0, drawn * SEG * 6);
    uploadPrefix(drawn * (SEG + 1) * 2, this.geo.attributes.position as THREE.BufferAttribute, this.geo.attributes.nedge as THREE.BufferAttribute, this.geo.attributes.ncore as THREE.BufferAttribute); // PERF-STRESS: live needles only
  }

  clear(): void { for (const n of this.needles) n.on = false; this.geo.setDrawRange(0, 0); }
}
